import { useCallback, useEffect, useState } from 'react';
import { Capsule } from '../../../types/capsule';
import { fetchCapsuleManifest, isCapsuleManifest } from '../data/capsuleClient';

interface UseCapsulesResult {
  capsules: Capsule[];
  loading: boolean;
  error: string | null;
  getCapsuleBySlug: (slug: string) => Capsule | undefined;
  refetch: () => Promise<void>;
}

export function useCapsules(): UseCapsulesResult {
  const [capsules, setCapsules] = useState<Capsule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCapsules = useCallback(async (bustCache = false) => {
    setLoading(true);
    setError(null);
    try {
      const manifest = await fetchCapsuleManifest(bustCache);
      if (!isCapsuleManifest(manifest)) {
        throw new Error('Capsule manifest not available');
      }
      setCapsules(manifest.capsules);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load capsules';
      setError(message === 'Capsule manifest not available' ? message : 'Failed to load capsules');
      setCapsules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCapsules();
  }, [fetchCapsules]);

  const getCapsuleBySlug = useCallback(
    (slug: string) => capsules.find((c) => c.slug === slug),
    [capsules]
  );

  return {
    capsules,
    loading,
    error,
    getCapsuleBySlug,
    refetch: () => fetchCapsules(true),
  };
}
